import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, RotateCcw, CheckCircle2, XCircle, Clock,
  Loader2, ExternalLink, FileText, Upload, X,
} from 'lucide-react';
import {
  fetchAdminRMA, patchAdminRMAUnit, closeAdminRMA, uploadAdminRMAAttachment,
} from '../../services/rmaService';
import { RMARequest, formatErpRmaNumber, formatNotaCredito } from '../../types';
import { toast } from 'sonner';
import { ConfirmActionDialog } from '../../components/admin/ConfirmActionDialog';
import { LoadError } from '../../components/rma/LoadError';
import { numeroDocumento } from '../../utils/numeroDocumento';
import { AllegatoRma } from '../../components/rma/AllegatoRma';

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  aperta:         { label: 'Aperta',                 color: 'bg-blue-100 text-blue-700' },
  in_lavorazione: { label: 'In Lavorazione',         color: 'bg-amber-100 text-amber-700' },
  chiusa:         { label: 'Chiusa',                 color: 'bg-gray-100 text-gray-600' },
};

const MOTIVI_SHORT: Record<string, string> = {
  difetto_stampa:              'Difetto stampa',
  prodotto_danneggiato:        'Danneggiato',
  qualita_non_conforme:        'Qualità non conforme',
  articolo_errato_ricevuto:    'Articolo errato',
  articolo_ordinato_erroneamente: 'Ordine erroneo',
  quantita_errata:             'Qtà errata',
  altro:                       'Altro',
};

const ACCEPT_ATTR = 'image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';

/** Riferimento completo del documento (D.D.T./fattura) di provenienza di un articolo,
 * es. "B/2025/A/1234". Il documento comanda: articoli con lo stesso SKU ma provenienti
 * da documenti diversi restano su righe/item distinti, non vengono mai fusi. */
function formatDocRef(item: any): string {
  if (!item.docTipork || !item.docNumdoc) return '—';
  const serie = (item.docSerie || '').trim();
  return [item.docTipork, item.docAnno, serie, item.docNumdoc].filter(v => v !== undefined && v !== null && v !== '').join('/');
}

function Attachment({ url, downloadUrl, mime, name, testid }: { url: string; downloadUrl?: string; mime?: string; name?: string; testid: string }) {
  return (
    <AllegatoRma
      url={url}
      downloadUrl={downloadUrl}
      mime={mime}
      name={name}
      alt={name || 'Allegato del reso'}
      testid={testid}
      imgClassName="h-20 w-auto rounded border object-cover"
      pdfClassName="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-red-50 hover:bg-red-100 text-xs font-medium text-red-700 transition-colors"
    />
  );
}

/** Modale "Respingi pezzo" — sostituisce il precedente pannello espanso
 * inline nella riga tabella (richiesta utente 26/02/2026). */
function RejectUnitModal({
  unit, rejectNote, setRejectNote, rejectFile, setRejectFile, onHandleFile,
  onConfirm, onCancel, saving, uploading,
}: {
  unit: any; rejectNote: string; setRejectNote: (v: string) => void;
  rejectFile: File | null; setRejectFile: (f: File | null) => void;
  onHandleFile: (f: File) => void; onConfirm: () => void; onCancel: () => void;
  saving: boolean; uploading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid={`admin-reject-modal-${unit.id}`}>
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl p-5 w-full max-w-lg mx-4">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          data-testid={`admin-reject-modal-close-${unit.id}`}
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <h2 className="font-semibold text-base">Respingi pezzo</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          <span className="font-mono font-semibold text-[hsl(214,58%,41%)]">{unit.sku}</span> — {unit.title}
        </p>

        <label className="text-[11px] font-semibold text-red-700 block mb-1.5">Motivazione respingimento *</label>
        <textarea
          value={rejectNote}
          onChange={e => setRejectNote(e.target.value)}
          placeholder="Spiega al cliente perché il pezzo viene respinto..."
          rows={3}
          className="w-full px-2.5 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-300 bg-white mb-3"
          data-testid={`admin-reject-note-${unit.id}`}
          autoFocus
        />

        <div className="flex items-center gap-2 mb-4">
          {rejectFile ? (
            <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border bg-white text-xs">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" /> {rejectFile.name}
              <button onClick={() => setRejectFile(null)} className="text-muted-foreground hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          ) : (
            <label className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border-2 border-dashed cursor-pointer text-xs hover:bg-secondary/40 transition-colors">
              <Upload className="w-3 h-3" /> Allega foto (opzionale)
              <input
                type="file"
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={e => e.target.files?.[0] && onHandleFile(e.target.files[0])}
                data-testid={`admin-reject-photo-${unit.id}`}
              />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-secondary/60 transition-colors"
            data-testid={`admin-reject-cancel-${unit.id}`}
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={saving || uploading}
            className="h-9 px-4 rounded-lg bg-red-500 text-white text-sm font-medium flex items-center gap-1.5 hover:bg-red-600 transition-colors disabled:opacity-50"
            data-testid={`admin-reject-confirm-${unit.id}`}
          >
            {(saving || uploading) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            Conferma respingimento
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminRMADetailPage() {
  const { numero } = useParams<{ numero: string }>();
  const navigate = useNavigate();
  const [rma, setRma] = useState<RMARequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingUnit, setSavingUnit] = useState<string | null>(null);

  // Pannello "Respingi": id unità attualmente in editing + draft locale
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectFile, setRejectFile] = useState<File | null>(null);
  const [rejectUploading, setRejectUploading] = useState(false);
  // Chiusura: conferma esplicita e un solo invio alla volta. Un doppio click
  // creava due Ordini RMA sul gestionale (audit RMA #18).
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!numero) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    fetchAdminRMA(numero)
      .then(data => { if (!cancelled) setRma(data); })
      .catch((e: any) => { if (!cancelled && e?.response?.status !== 404) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [numero, attempt]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground" data-testid="admin-rma-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento pratica RMA...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6">
        <LoadError message="Non è stato possibile caricare la pratica RMA." onRetry={() => setAttempt(a => a + 1)} testId="admin-rma-detail-error" />
      </div>
    );
  }

  if (!rma) {
    return (
      <div className="p-6 text-center" data-testid="admin-rma-detail-not-found">
        <p className="text-sm text-muted-foreground mb-3">Pratica RMA non trovata.</p>
        <Link to="/admin/rma" className="text-sm text-[hsl(214,58%,41%)] font-medium">Torna all'elenco</Link>
      </div>
    );
  }

  const rmaAny = rma as any;
  const allUnits = rmaAny.items.flatMap((i: any) => i.units ?? []);
  const st = STATUS_MAP[rma.status] ?? STATUS_MAP.aperta;
  const pendingN = allUnits.filter((u: any) => (u.adminDecision ?? 'pending') === 'pending').length;
  const canClose = rma.status !== 'chiusa' && (allUnits.length === 0 || pendingN === 0);
  const isClosed = rma.status === 'chiusa';
  const rows = allUnits.length > 0
    ? rmaAny.items.flatMap((item: any) =>
        (item.units ?? []).map((unit: any, unitIdx: number) => ({ item, unit, unitIdx, totalInItem: item.units?.length ?? 1 }))
      )
    : [];
  const rejectingUnit = rows.find((r: any) => r.unit.id === rejectingId)?.unit ?? null;

  const approve = async (unitId: string) => {
    setSavingUnit(unitId);
    try {
      const updated = await patchAdminRMAUnit(rma.id, unitId, { decision: 'approvata' });
      setRma(updated);
      toast.success(`Pezzo ${unitId}: approvato`);
    } catch {
      toast.error('Errore durante il salvataggio della decisione.');
    } finally {
      setSavingUnit(null);
    }
  };

  const openRejectPanel = (unit: any) => {
    setRejectingId(unit.id);
    setRejectNote(unit.adminNote || '');
    setRejectFile(null);
  };

  const closeRejectPanel = () => {
    setRejectingId(null);
    setRejectNote('');
    setRejectFile(null);
  };

  const confirmReject = async (unitId: string) => {
    if (!rejectNote.trim()) {
      toast.error('Inserisci una motivazione per il respingimento.');
      return;
    }
    setSavingUnit(unitId);
    try {
      let adminPhotoPath: string | undefined;
      let adminPhotoName: string | undefined;
      let adminPhotoMime: string | undefined;
      if (rejectFile) {
        setRejectUploading(true);
        const uploaded = await uploadAdminRMAAttachment(rejectFile);
        adminPhotoPath = uploaded.path;
        adminPhotoName = uploaded.name;
        adminPhotoMime = uploaded.mime;
      }
      const updated = await patchAdminRMAUnit(rma.id, unitId, {
        decision: 'respinta',
        note: rejectNote.trim(),
        ...(adminPhotoPath ? { adminPhotoPath, adminPhotoName, adminPhotoMime } : {}),
      });
      setRma(updated);
      toast.success(`Pezzo ${unitId}: respinto`);
      closeRejectPanel();
    } catch (err: any) {
      toast.error('Errore durante il respingimento', {
        description: err?.response?.data?.detail || 'Riprova più tardi.',
      });
    } finally {
      setRejectUploading(false);
      setSavingUnit(null);
    }
  };

  const approvedN = allUnits.filter((u: any) => u.adminDecision === 'approvata').length;

  const closePractice = async () => {
    if (closing) return;
    setClosing(true);
    try {
      const updated = await closeAdminRMA(rma.id);
      setRma(updated);
      toast.success(`RMA ${rma.id} chiusa.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore durante la chiusura della pratica.');
      fetchAdminRMA(rma.id).then(setRma).catch(() => {});
    } finally {
      setClosing(false);
    }
  };

  const handleRejectFile = (file: File) => {
    const mime = file.type || '';
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const accepted = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(mime) || ['jpg', 'jpeg', 'png', 'pdf'].includes(ext);
    if (!accepted) {
      toast.error('Formato non supportato', { description: 'Sono ammessi solo JPG, PNG o PDF.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande', { description: 'Dimensione massima 5 MB.' });
      return;
    }
    setRejectFile(file);
  };

  return (
    <div className="p-6 w-full" data-testid="admin-rma-detail-page">
      {/* Header */}
      <button
        onClick={() => navigate('/admin/rma')}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="admin-rma-detail-back"
      >
        <ChevronLeft className="w-4 h-4" /> Torna all'elenco RMA
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-3">
          <RotateCcw className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold tracking-tight font-mono">{rma.id}</h1>
          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>
            {st.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {rmaAny.documentoUrl && (
            <a
              href={rmaAny.documentoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-rma-detail-apri-modulo"
              className="h-8 px-3 rounded-lg flex items-center gap-1.5 bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)] transition-colors text-xs font-medium"
            >
              <FileText className="w-3.5 h-3.5" /> Modulo RMA <ExternalLink className="w-3 h-3 opacity-80" />
            </a>
          )}
          {rmaAny.documentoEsitoUrl && (
            <a
              href={rmaAny.documentoEsitoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-rma-detail-apri-esito"
              className="h-8 px-3 rounded-lg flex items-center gap-1.5 bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)] transition-colors text-xs font-medium"
            >
              <FileText className="w-3.5 h-3.5" /> Esito RMA <ExternalLink className="w-3 h-3 opacity-80" />
            </a>
          )}
          {canClose && (
            <button
              onClick={() => setConfirmClose(true)}
              disabled={closing}
              className="h-8 px-3 rounded-lg border text-xs font-medium hover:bg-secondary/60 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="admin-rma-detail-close"
            >
              {closing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {closing ? 'Chiusura in corso…' : 'Chiudi Pratica'}
            </button>
          )}
        </div>
      </div>
      <div className="text-xs text-muted-foreground mb-6">
        Documento: {numeroDocumento(rma.orderId)} · {rma.date}
        {rmaAny.ragioneSocialeCliente && <> · {rmaAny.ragioneSocialeCliente}</>}
        {pendingN > 0 && !isClosed && <span className="text-amber-600 font-medium"> · {pendingN} in attesa di decisione</span>}
        {formatErpRmaNumber(rmaAny.erpRma) && (
          <span data-testid="admin-rma-detail-erp-numero"> · <span className="font-mono font-semibold text-foreground">{formatErpRmaNumber(rmaAny.erpRma)}</span></span>
        )}
      </div>
      {isClosed && rmaAny.erpRma && (
        (rma.noteCredito ?? []).length > 0 ? (
          <div className="text-xs mb-6 -mt-4 space-y-0.5" data-testid="admin-rma-detail-note-credito">
            {rma.noteCredito!.map((n) => (
              <div key={`${n.anno}-${n.serie}-${n.numdoc}`} className="font-medium text-emerald-700">{formatNotaCredito(n)}</div>
            ))}
          </div>
        ) : (
          <div className="text-xs mb-6 -mt-4 text-amber-700" data-testid="admin-rma-detail-nota-da-emettere">
            Nota di credito da emettere su NTS: scrivi <span className="font-mono font-semibold">{rma.id}</span> nel
            campo Riferimento, e il portale la collega a questa pratica entro 30 minuti.
          </div>
        )
      )}

      {/* Articoli — valutazione pezzo per pezzo, tabellare a larghezza piena.
          Nota: la motivazione del cliente è espressa per singolo articolo
          (colonna "Motivo cliente" sotto), non un'unica motivazione generale
          per l'intera pratica. */}
      {allUnits.length > 0 ? (
        <div className="rounded-xl border bg-white overflow-x-auto">
          <table className="w-full text-sm border-collapse" style={{ minWidth: 1300 }}>
            <thead>
              <tr className="bg-[hsl(214,64%,29%)] text-[11px] font-semibold text-white uppercase tracking-wider">
                <th className="py-2.5 px-3 text-center w-11 sticky left-0 z-20 bg-[hsl(214,64%,29%)]">#</th>
                <th className="py-2.5 px-3 text-left w-52 sticky z-20 bg-[hsl(214,64%,29%)]" style={{ left: 44 }}>Articolo</th>
                <th className="py-2.5 px-3 text-left whitespace-nowrap">Documento</th>
                <th className="py-2.5 px-3 text-left">Motivo cliente</th>
                <th className="py-2.5 px-3 text-center">Allegato</th>
                <th className="py-2.5 px-3 text-right">Prezzo</th>
                <th className="py-2.5 px-3 text-center">Stato</th>
                <th className="py-2.5 px-3 text-left">Note / Allegato admin</th>
                {/* Fissa a destra come "Articolo" a sinistra: a 1440 px la tabella
                    è più larga dell'area e le azioni finivano fuori schermo (audit RMA #23). */}
                <th className="py-2.5 px-3 text-center sticky right-0 z-20 bg-[hsl(214,64%,29%)]">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ item, unit, unitIdx, totalInItem }: any, rowIdx: number) => {
                const motivoLabel = MOTIVI_SHORT[unit.motivo] ?? unit.motivo;
                const rowBg = rowIdx % 2 === 1 ? 'bg-secondary/20' : 'bg-white';
                const numBg = unit.adminDecision === 'approvata' ? 'bg-emerald-50 text-emerald-700' :
                  unit.adminDecision === 'respinta' ? 'bg-red-50 text-red-700' : `${rowBg} text-[hsl(214,58%,41%)]`;
                return (
                  <tr key={unit.id} className={`border-b last:border-b-0 ${rowBg}`} data-testid={`admin-unit-${unit.id}`}>
                    <td className={`py-3 px-3 text-center align-top text-sm font-bold sticky left-0 z-10 ${numBg}`}>
                      {unitIdx + 1}
                    </td>

                    <td className={`py-3 px-3 align-top sticky z-10 ${rowBg}`} style={{ left: 44 }}>
                      <div className="text-sm font-mono font-semibold text-[hsl(214,58%,41%)]">{unit.sku}</div>
                      <div className="text-xs text-muted-foreground">{unit.title}</div>
                      {totalInItem > 1 && (
                        <span className="text-[10px] text-muted-foreground">pz {unitIdx + 1}/{totalInItem}</span>
                      )}
                    </td>

                    <td className="py-3 px-3 align-top whitespace-nowrap">
                      <span className="text-xs font-mono text-muted-foreground" title="Documento di provenienza (D.D.T./fattura)">
                        {formatDocRef(item)}
                      </span>
                    </td>

                    <td className="py-3 px-3 align-top max-w-[280px]">
                      <div className="text-xs font-medium text-foreground">{motivoLabel}</div>
                      {unit.descrizione && (
                        <p className="text-xs text-muted-foreground whitespace-pre-line break-words"><span className="font-medium text-foreground not-italic">Note del cliente:</span> {unit.descrizione}</p>
                      )}
                    </td>

                    <td className="py-3 px-3 text-center align-top">
                      {unit.photoUrl ? (
                        <Attachment url={unit.photoUrl} downloadUrl={unit.photoDownloadUrl} mime={unit.photoMime} name={unit.photoName} testid={`rma-admin-attachment-${unit.id}`} />
                      ) : (
                        <span className="text-muted-foreground text-xs">&mdash;</span>
                      )}
                    </td>

                    <td className="py-3 px-3 text-right align-top whitespace-nowrap">
                      {unit.prezzoPagato > 0 ? (
                        <span className="text-xs text-muted-foreground">€ {unit.prezzoPagato.toFixed(2).replace('.', ',')}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">&mdash;</span>
                      )}
                    </td>

                    <td className="py-3 px-3 text-center align-top">
                      <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${
                        unit.adminDecision === 'approvata' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' :
                        unit.adminDecision === 'respinta'  ? 'border-red-300 bg-red-50 text-red-700' :
                        'border-border bg-[hsl(var(--dtk-canvas))] text-muted-foreground'
                      }`}>
                        {unit.adminDecision === 'pending' ? 'In attesa' : unit.adminDecision === 'approvata' ? 'Approvato' : 'Respinto'}
                      </span>
                    </td>

                    <td className="py-3 px-3 align-top max-w-[300px]">
                      {unit.adminDecision === 'respinta' && (unit.adminNote || unit.adminPhotoUrl) ? (
                        <div className="space-y-1.5">
                          {unit.adminNote && (
                            <p className="text-xs text-red-800" title={unit.adminNote}>{unit.adminNote}</p>
                          )}
                          {unit.adminPhotoUrl && (
                            <Attachment url={unit.adminPhotoUrl} downloadUrl={unit.adminPhotoDownloadUrl} mime={unit.adminPhotoMime} name={unit.adminPhotoName} testid={`rma-admin-reject-attachment-${unit.id}`} />
                          )}
                        </div>
                      ) : unit.adminDecision !== 'respinta' && unit.adminNote ? (
                        <p className="text-xs text-muted-foreground italic" title={unit.adminNote}>{unit.adminNote}</p>
                      ) : (
                        <span className="text-muted-foreground text-xs">&mdash;</span>
                      )}
                    </td>

                    <td className={`py-3 px-3 align-top sticky right-0 z-10 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.18)] ${rowIdx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}`}>
                      {!isClosed && (
                        <div className="flex flex-col gap-1.5 items-center">
                          <button
                            onClick={() => approve(unit.id)}
                            disabled={savingUnit === unit.id}
                            className={`h-7 px-3 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 whitespace-nowrap ${
                              unit.adminDecision === 'approvata' ? 'bg-emerald-500 text-white' : 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                            }`}
                            data-testid={`admin-unit-approve-${unit.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" /> Approva
                          </button>
                          <button
                            onClick={() => openRejectPanel(unit)}
                            disabled={savingUnit === unit.id}
                            className={`h-7 px-3 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 whitespace-nowrap ${
                              unit.adminDecision === 'respinta' ? 'bg-red-500 text-white' : 'border border-red-300 text-red-700 hover:bg-red-50'
                            }`}
                            data-testid={`admin-unit-reject-${unit.id}`}
                          >
                            <XCircle className="w-3 h-3" /> {unit.adminDecision === 'respinta' ? 'Modifica' : 'Respingi'}
                          </button>
                          {savingUnit === unit.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-xl border bg-white p-5">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Articoli</div>
          {rmaAny.items.map((item: any) => (
            <div key={item.sku} className="flex items-center justify-between p-2 rounded-lg border bg-[hsl(var(--dtk-canvas))] text-xs mb-1">
              <span><span className="font-mono text-[hsl(214,58%,41%)]">{item.sku}</span> — {item.title}</span>
              <span className="text-muted-foreground">Qtà: {item.qtyReturned}</span>
            </div>
          ))}
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Pratica legacy senza valutazione pezzo per pezzo.
          </p>
        </div>
      )}

      {rejectingUnit && (
        <RejectUnitModal
          unit={rejectingUnit}
          rejectNote={rejectNote}
          setRejectNote={setRejectNote}
          rejectFile={rejectFile}
          setRejectFile={setRejectFile}
          onHandleFile={handleRejectFile}
          onConfirm={() => confirmReject(rejectingUnit.id)}
          onCancel={closeRejectPanel}
          saving={savingUnit === rejectingUnit.id}
          uploading={rejectUploading}
        />
      )}
      <ConfirmActionDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        variant="warning"
        title={`Chiudere la pratica ${rma.id}?`}
        description={approvedN > 0
          ? ((n: number) => `${n} ${n === 1 ? 'pezzo approvato' : 'pezzi approvati'}: verrà creato l'Ordine RMA sul gestionale. La pratica non sarà più modificabile.`)(approvedN)
          : 'Nessun pezzo approvato: la pratica si chiude senza creare nulla sul gestionale.'}
        confirmLabel="Chiudi pratica"
        onConfirm={closePractice}
      />
    </div>
  );
}
